'use client';

// biome-ignore lint/performance/noNamespaceImport: library designed for namespace import
import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { useFormContext } from 'react-hook-form';
import { Icon } from '@/app_components/common/Icon';
import { Link } from '@/app_components/common/Link';
import { FormControl, FormField, FormItem } from '@/app_components/ui/form';
import type { UserConsentLocation } from '@/app_lib/types/accounts/signin';

const IMPLICIT_CONSENT_LOCATIONS = new Set<UserConsentLocation>(['US', 'DEFAULT']);

const EMAIL_CONSENT_TEXT: Partial<Record<UserConsentLocation, string>> = {
  // biome-ignore lint/style/useNamingConvention: UserConsentLocation keys are uppercase by convention
  CA: 'Fortune may send me Fortune newsletters and offer me other Fortune products and services by email. I can opt out at any time.',
  // biome-ignore lint/style/useNamingConvention: UserConsentLocation keys are uppercase by convention
  GHPSC:
    'Fortune may send me newsletters, information, and offers relating to other Fortune products and services by email. I may opt-out at any time by clicking the unsubscribe link in those emails.',
  // biome-ignore lint/style/useNamingConvention: UserConsentLocation keys are uppercase by convention
  UK_EEA:
    'Fortune may send me newsletters, information, and offers relating to other Fortune products and services by email. I may opt-out by unchecking the box or at any time by clicking the unsubscribe link in those emails.',
};

interface SignInTermsAndMarketingProps {
  consentLocation: UserConsentLocation;
}

export const SignInTermsAndMarketing = ({ consentLocation }: SignInTermsAndMarketingProps) => {
  const { control } = useFormContext();
  const isImplicitConsent = IMPLICIT_CONSENT_LOCATIONS.has(consentLocation);
  const emailConsentText = EMAIL_CONSENT_TEXT[consentLocation] ?? '';

  return (
    <FormField
      control={control}
      name='registrationConsent'
      render={({ field }) => (
        <FormItem className='flex flex-col items-start space-y-0 gap-sm font-graphik-compact'>
          <p className='typography-sm font-medium'>
            By creating an account, I have read and agreed to Fortune&#39;s{' '}
            <Link
              className='text-accent-primary underline'
              href='https://fortune.com/terms-of-use/'
              target='_blank'
            >
              Terms of Use
            </Link>{' '}
            and{' '}
            <Link
              className='text-accent-primary underline'
              href='/privacy-policy/'
              target='_blank'
            >
              Privacy Policy
            </Link>
            .
          </p>
          {isImplicitConsent ? (
            <FormControl>
              <input
                checked={Boolean(field.value)}
                id='registrationConsent'
                type='hidden'
                {...field}
              />
            </FormControl>
          ) : (
            <div className='flex items-start gap-sm'>
              <FormControl>
                <RadixCheckbox.Root
                  aria-describedby='marketing-consent-text'
                  aria-label='Email marketing consent'
                  checked={field.value}
                  className="relative m-1 shrink-0 border rounded-xs size-4.5 cursor-pointer hover:bg-blue-100 before:content-[''] before:absolute before:-inset-2 md:before:inset-0 [&[data-state=checked]]:icon-white [&[data-state=checked]]:bg-accent [&[data-state=checked]]:stroke-accent"
                  id='registrationConsent'
                  onCheckedChange={field.onChange}
                >
                  <RadixCheckbox.Indicator className='flex items-center justify-center w-full h-full'>
                    <Icon
                      className='w-3.5 h-3.5'
                      icon='Check'
                      size='lg'
                    />
                  </RadixCheckbox.Indicator>
                </RadixCheckbox.Root>
              </FormControl>
              <div
                className='typography-xs'
                id='marketing-consent-text'
              >
                {emailConsentText}
              </div>
            </div>
          )}
        </FormItem>
      )}
    />
  );
};
